import { definePrice } from './calculateShowFees';
import type { ICartItem } from '../hooks/useCart';

type TicketOption = {
    id?: number;
    name?: string;
};

type TicketType = {
    id?: number;
    name?: string;
    priceMatrix?: Record<string, number>;
};

type ShowForGa4 = {
    title?: string;
};

export type Ga4Item = {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
};

type Ga4CartItem = Pick<
    ICartItem,
    'ticketTypeId' | 'ticketTypeName' | 'ticketOptionId' | 'ticketOptionName' | 'price' | 'quantity'
>;

const toMajorUnit = (cents: number): number => Number((cents / 100).toFixed(2));

const buildItemName = (ticketTypeName?: string, ticketOptionName?: string): string => {
    return [ticketTypeName || 'Ticket', ticketOptionName].filter(Boolean).join(' - ');
};

export const buildGa4ItemFromCartItem = (item: Ga4CartItem, showTitle?: string): Ga4Item => ({
    item_id: `ticket-${item.ticketTypeId}-option-${item.ticketOptionId}`,
    item_name: buildItemName(item.ticketTypeName, item.ticketOptionName),
    item_category: showTitle || 'Show',
    price: toMajorUnit(item.price || 0),
    quantity: item.quantity || 1,
});

export const buildGa4ItemsFromCart = (cart: Ga4CartItem[] = [], showTitle?: string): Ga4Item[] =>
    cart.map((item) => buildGa4ItemFromCartItem(item, showTitle));

export const buildGa4ItemsFromTicketMatrix = (
    ticketTypes: TicketType[] = [],
    ticketOptions: TicketOption[] = [],
    show: ShowForGa4,
): Ga4Item[] => {
    const items: Ga4Item[] = [];

    for (const ticketType of ticketTypes) {
        for (const ticketOption of ticketOptions) {
            if (!ticketType.id || !ticketOption.id || !ticketOption.name) continue;

            try {
                const price = definePrice(ticketType.priceMatrix || {}, ticketOption.name);
                items.push({
                    item_id: `ticket-${ticketType.id}-option-${ticketOption.id}`,
                    item_name: buildItemName(ticketType.name, ticketOption.name),
                    item_category: show.title || 'Show',
                    price: toMajorUnit(price || 0),
                    quantity: 1,
                });
            } catch {
                // The UI already hides options whose price cannot be resolved.
            }
        }
    }

    return items;
};

export const sumGa4ItemsValue = (items: Ga4Item[]): number =>
    Number(
        items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0).toFixed(2),
    );
